/**
 * AdminDashboard — Clean Swiss-styled hub for the entire admin area.
 *
 * Single source of truth for navigation. Two clear sections:
 *   1. OPERATIONS — deep links into Command Center sub-tabs (no duplicate pages)
 *   2. SITE EDITING — tools that change what the public site shows
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, LogOut, RefreshCw, ChevronRight,
  Activity, Calendar, Wallet, Users, Network, ClipboardList,
  Image as ImageIcon, FileText, Tag, Palette, Edit3,
  Truck, Navigation, ShieldCheck, Inbox, DollarSign,
} from 'lucide-react';
import { getAdminStats, logout } from '../api';
import { SwissCard, KpiCard, SectionHeader, Overline } from '../components/layout/SwissUI';

const fmt$ = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

// ── Operations cards (deep-link to /command-center?tab=XXX) ──
const OPERATIONS_GROUPS = [
  {
    bucket: 'Command',
    icon: Activity,
    items: [
      { tab: 'ops',         label: 'Overview',         desc: 'Pipeline · KPIs · roll-ups' },
      { tab: 'dailyops',    label: 'Daily Ops',        desc: 'Daily P&L · ops log v3' },
    ],
  },
  {
    bucket: 'Operations',
    icon: Calendar,
    items: [
      { tab: 'quotebookings', label: 'Quotes & Bookings', desc: 'Intake triage · bookings · rentals' },
      { tab: 'contracts',   label: 'Contracts & Calendar', desc: 'Revenue calendar · contracts · invoices' },
      { tab: 'contracts',     label: 'Contracts',          desc: 'E-sign · production sheet' },
    ],
  },
  {
    bucket: 'Financials',
    icon: Wallet,
    items: [
      { tab: 'weeklypl',  label: 'Weekly & P&L',  desc: 'Weekly review · vehicle P&L · maintenance · condition reports' },
      { tab: 'monthlypl', label: 'Monthly P&L',  desc: 'Master 7-pillar tracker' },
    ],
  },
  {
    bucket: 'Network',
    icon: Network,
    items: [
      { tab: 'teampay', label: 'Team & Pay',  desc: 'Drivers · payroll · affiliates · partners · owner payouts' },
      { tab: 'clients', label: 'Clients',     desc: 'CRM · consulting · VIP · follow-ups' },
      { tab: 'quo',     label: 'Quo · SMS',   desc: 'OpenPhone contacts & messages' },
    ],
  },
];

// ── Site Editing tools (separate routes — they edit public site content) ──
const SITE_EDITING = [
  { to: '/admin/fleet-media',    icon: ImageIcon,   label: 'Fleet Media',     desc: 'Upload vehicle photos & videos' },
  { to: '/admin/photo-picker',   icon: Palette,     label: 'Photo Picker',    desc: 'Pick gallery hero shots' },
  { to: '/cms-admin',            icon: Edit3,       label: 'CMS Editor',      desc: 'Public site copy + page content' },
  { to: '/admin/rental-pricing', icon: Tag,         label: 'Rental Pricing',  desc: 'Self-drive rate sheet' },
  { to: '/admin/pricing-hq',     icon: DollarSign,  label: 'Pricing HQ',      desc: 'Chauffeured rate sheet · vehicles' },
];

// ── System / Power-user ──
const SYSTEM_TOOLS = [
  { to: '/admin/tracking', icon: Navigation,  label: 'Live Tracking', desc: 'Real-time vehicle locations' },
  { to: '/admin/users',    icon: ShieldCheck, label: 'Admin Users',   desc: 'Manage admin accounts' },
  { to: '/staff',          icon: Truck,       label: 'Staff Portal',  desc: 'Ground-team mobile portal', external: true },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (location.state?.user) {
        setUser(location.state.user);
        window.history.replaceState({}, document.title);
      } else if (!user) {
        try {
          const token = localStorage.getItem('ace_admin_token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include', headers });
          if (!r.ok) {
            if (location.hash?.includes('session_id=')) return; // OAuth callback handler below
            // No session at all — bounce to either Google login or mobile unlock
            if (/Mobi|Android/i.test(navigator.userAgent)) navigate('/m');
            else navigate('/admin/login');
            return;
          }
          const d = await r.json();
          if (!cancelled) { setUser(d); navigate('/os', { replace: true }); return; }
        } catch { if (!cancelled) navigate('/admin/login'); return; }
      }
      try {
        const s = await getAdminStats();
        if (!cancelled) setStats(s);
      } catch { /* non-fatal */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (location.hash?.includes('session_id=')) {
    const AuthCallback = React.lazy(() => import('./AuthCallback'));
    return (
      <React.Suspense fallback={<div className="min-h-screen bg-[#F9F9FA] flex items-center justify-center"><div className="animate-spin rounded-none h-10 w-10 border-2 border-[#E52B20] border-t-transparent" /></div>}>
        <AuthCallback />
      </React.Suspense>
    );
  }

  const handleLogout = async () => { try { await logout(); navigate('/admin/login'); } catch { /* */ } };

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A]" data-testid="admin-dashboard">
      {/* ── Sticky Header ── */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-8 w-8 bg-[#0A0A0A] text-white flex items-center justify-center font-black text-sm">A</div>
            <div>
              <p className="text-sm font-black tracking-tight">ACE Admin</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#666]">Dashboard</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setReloadKey(k => k + 1)} className="h-9 w-9 border border-[#E5E5E5] hover:border-[#0A0A0A] bg-white flex items-center justify-center transition-colors" data-testid="admin-refresh" title="Refresh stats">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {user && (
              <div className="flex items-center gap-2 h-9 px-3 border border-[#E5E5E5] bg-white">
                {user.picture && <img src={user.picture} alt={user.name} className="h-5 w-5 object-cover" />}
                <span className="text-[11px] font-bold uppercase tracking-wider hidden sm:block truncate max-w-[160px]">{user.email}</span>
              </div>
            )}
            <button onClick={handleLogout} className="h-9 px-3 border border-[#E5E5E5] hover:border-[#E52B20] hover:text-[#E52B20] bg-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* ── Welcome ── */}
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E52B20] font-bold mb-2">Welcome back</p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{user?.name?.split(' ')[0] || 'Admin'}.</h1>
          <p className="text-sm text-[#666] mt-1">Single source of truth for everything inside ACE.</p>
        </div>

        {/* ── At-a-glance KPIs ── */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="admin-kpi-strip">
            <KpiCard label="Total Clients" value={stats.contacts?.total || 0} icon={Users} onClick={() => navigate('/command-center?tab=clients')} testid="admin-kpi-clients" />
            <KpiCard label="Bookings" value={stats.bookings?.total || 0} icon={Calendar} onClick={() => navigate('/command-center?tab=quotebookings')} testid="admin-kpi-bookings" />
            <KpiCard label="Open Consultations" value={stats.consultations?.new || 0} icon={Inbox} tone="danger" onClick={() => navigate('/command-center?tab=quotebookings')} testid="admin-kpi-intake" />
            <KpiCard label="Total Revenue" value={fmt$(stats.payments?.total_revenue || 0)} icon={DollarSign} tone="success" onClick={() => navigate('/command-center?tab=monthlypl')} testid="admin-kpi-revenue" />
          </div>
        )}

        {/* ── SECTION 1 · OPERATIONS ── */}
        <div className="mb-12">
          <SectionHeader
            overline="Section 1 · Run the business"
            title="Operations"
            action={
              <Link to="/command-center" className="h-9 px-4 bg-[#E52B20] hover:bg-[#C92218] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors" data-testid="open-command-center">
                Open Command Center <ChevronRight className="h-3 w-3" />
              </Link>
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {OPERATIONS_GROUPS.map(g => {
              const Icon = g.icon;
              return (
                <SwissCard key={g.bucket} className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="h-4 w-4 text-[#E52B20]" strokeWidth={2} />
                    <Overline>{g.bucket}</Overline>
                  </div>
                  <div className="space-y-1">
                    {g.items.map(it => (
                      <Link
                        key={it.tab}
                        to={`/command-center?tab=${it.tab}`}
                        className="flex items-center justify-between gap-3 px-3 py-2 -mx-3 hover:bg-[#F9F9FA] group"
                        data-testid={`ops-link-${it.tab}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-bold">{it.label}</p>
                          <p className="text-[11px] text-[#666]">{it.desc}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-[#E5E5E5] group-hover:text-[#0A0A0A] shrink-0" />
                      </Link>
                    ))}
                  </div>
                </SwissCard>
              );
            })}
          </div>
        </div>

        {/* ── SECTION 2 · SITE EDITING ── */}
        <div className="mb-12">
          <SectionHeader
            overline="Section 2 · Edit what the public sees"
            title="Site Editing"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {SITE_EDITING.map(it => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  className="block p-4 bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] hover:shadow-[4px_4px_0px_#0A0A0A] transition-all"
                  data-testid={`site-edit-${it.to.split('/').pop()}`}
                >
                  <Icon className="h-5 w-5 text-[#E52B20] mb-3" strokeWidth={2} />
                  <p className="text-sm font-bold">{it.label}</p>
                  <p className="text-[10px] text-[#666] mt-0.5 leading-tight">{it.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── SECTION 3 · SYSTEM ── */}
        <div>
          <SectionHeader overline="Section 3" title="System" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SYSTEM_TOOLS.map(it => {
              const Icon = it.icon;
              const Comp = it.external ? 'a' : Link;
              const linkProps = it.external ? { href: it.to, target: '_blank', rel: 'noreferrer' } : { to: it.to };
              return (
                <Comp
                  key={it.to}
                  {...linkProps}
                  className="flex items-center gap-3 p-3 bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
                  data-testid={`system-${it.to.split('/').pop()}`}
                >
                  <Icon className="h-4 w-4 text-[#666]" strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="text-sm font-bold">{it.label}</p>
                    <p className="text-[10px] text-[#666] truncate">{it.desc}</p>
                  </div>
                  {it.external && <ChevronRight className="h-3 w-3 text-[#E5E5E5] ml-auto" />}
                </Comp>
              );
            })}
          </div>
        </div>

        {/* ── Quick legend ── */}
        <div className="mt-12 p-4 bg-white border border-[#E5E5E5]">
          <Overline>What lives where?</Overline>
          <ul className="text-xs text-[#666] mt-2 space-y-1 leading-relaxed">
            <li><strong className="text-[#0A0A0A]">Day-to-day work →</strong> Command Center (Section 1 above). Bookings, intake, drivers, P&L, clients all live there.</li>
            <li><strong className="text-[#0A0A0A]">Changing the public site →</strong> Section 2 (Fleet Media, Photos, CMS, Pricing).</li>
            <li><strong className="text-[#0A0A0A]">Live updates from the road →</strong> Open <Link to="/staff" target="_blank" className="text-[#E52B20] underline">Staff Portal</Link> from your phone (passcode-gated).</li>
            <li><strong className="text-[#0A0A0A]">Quick search anywhere →</strong> Inside Command Center, press <kbd className="font-mono text-[10px] px-1 border border-[#E5E5E5]">⌘K</kbd> to jump to any booking, client, vehicle or partner.</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
